"use client";

import { c as _c } from "react/compiler-runtime";
import { BrainIcon, FileTextIcon, FolderIcon, Loader2Icon, MessageSquarePlusIcon, MessageSquareIcon, PlusIcon, TrashIcon, UploadIcon, AlertCircleIcon } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreateChatProjectDialog } from "@/components/playground/create-chat-project-dialog";
import { ProjectsSidebar } from "@/components/playground/projects-sidebar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { PROJECT_BINARY_FILE_MAX_BYTES, PROJECT_DESCRIPTION_MAX, PROJECT_FILE_MAX_BYTES, PROJECT_MEMORY_MAX, PROJECT_NAME_MAX, useChatProject, useChatProjects, useCreateProjectMemory, useDeleteChatProject, useDeleteProjectFile, useDeleteProjectMemory, useProjectChats, useProjectMemories, useUpdateChatProject, useUpdateProjectMemory, useUploadProjectFile } from "@/hooks/useChatProjects";
import { useOrganization } from "@/hooks/useOrganization";
import { cn, withOrgParam } from "@/lib/utils";
import type { ChatProject, ChatProjectFile, ChatProjectMemory } from "@/hooks/useChatProjects";
import type { Organization } from "@/lib/types";

// Text-based formats are read client-side; PDF and Excel files are sent as
// base64 and their text is extracted server-side.
const ACCEPTED_FILE_EXTENSIONS = ".txt,.md,.markdown,.mdx,.csv,.tsv,.json,.yaml,.yml,.xml,.html,.log,.js,.jsx,.ts,.tsx,.py,.rb,.go,.rs,.java,.c,.cpp,.h,.css,.pdf,.xlsx";
const BINARY_FILE_EXTENSIONS = [".pdf", ".xlsx"];
function isBinaryKnowledgeFile(name: string) {
  const lower = name.toLowerCase();
  return BINARY_FILE_EXTENSIONS.some(ext => lower.endsWith(ext));
}
function toBase64(bytes: Uint8Array) {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
}
function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
interface ProjectsPageClientProps {
  selectedOrganization: Organization | null;
  initialProjectId: string | null;
}
export default function ProjectsPageClient(t0) {
  const $ = _c(60);
  const {
    selectedOrganization,
    initialProjectId
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("orgId");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const orgIdParam = t1;
  let t2;
  if ($[2] !== orgIdParam) {
    t2 = path => withOrgParam(path, orgIdParam);
    $[2] = orgIdParam;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const withOrg = t2;
  const {
    organization: chatOrg
  } = useOrganization();
  const resolvedOrgId = selectedOrganization?.id ?? chatOrg?.id;
  const {
    data,
    isLoading
  } = useChatProjects(resolvedOrgId);
  let t3;
  if ($[4] !== data?.projects) {
    t3 = data?.projects as ChatProject[] | undefined ?? [];
    $[4] = data?.projects;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const projects = t3;
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId);
  const [createOpen, setCreateOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  let t4;
  let t5;
  if ($[6] !== initialProjectId) {
    t4 = () => {
      setSelectedProjectId(initialProjectId);
    };
    t5 = [initialProjectId];
    $[6] = initialProjectId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const deleteProject = useDeleteChatProject();
  let t6;
  if ($[9] !== projects || $[10] !== selectedProjectId) {
    t6 = projects.find(p => p.id === selectedProjectId) ?? null;
    $[9] = projects;
    $[10] = selectedProjectId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selectedProject = t6;
  let t7;
  if ($[12] !== router || $[13] !== withOrg) {
    t7 = projectId => {
      setSelectedProjectId(projectId);
      router.replace(withOrg(`/projects?id=${projectId}`));
    };
    $[12] = router;
    $[13] = withOrg;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleSelectProject = t7;
  let t8;
  if ($[15] !== router || $[16] !== withOrg) {
    t8 = project => {
      setSelectedProjectId(project.id);
      router.replace(withOrg(`/projects?id=${project.id}`));
    };
    $[15] = router;
    $[16] = withOrg;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleCreateSuccess = t8;
  let t9;
  if ($[18] !== deleteProject || $[19] !== projectToDelete || $[20] !== router || $[21] !== selectedProjectId || $[22] !== withOrg) {
    t9 = () => {
      if (!projectToDelete) {
        return;
      }
      deleteProject.mutate({
        params: {
          path: {
            id: projectToDelete.id
          }
        }
      }, {
        onSuccess: () => {
          if (selectedProjectId === projectToDelete.id) {
            setSelectedProjectId(null);
            router.replace(withOrg("/projects"));
          }
          setProjectToDelete(null);
        }
      });
    };
    $[18] = deleteProject;
    $[19] = projectToDelete;
    $[20] = router;
    $[21] = selectedProjectId;
    $[22] = withOrg;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleDeleteConfirm = t9;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setCreateOpen(true);
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== handleSelectProject || $[26] !== isLoading || $[27] !== projects || $[28] !== selectedOrganization || $[29] !== selectedProjectId) {
    t11 = <ProjectsSidebar projects={projects} selectedProjectId={selectedProjectId} onSelectProject={handleSelectProject} isLoading={isLoading} onCreateOpen={t10} selectedOrganization={selectedOrganization} />;
    $[25] = handleSelectProject;
    $[26] = isLoading;
    $[27] = projects;
    $[28] = selectedOrganization;
    $[29] = selectedProjectId;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <header className="bg-background flex items-center border-b p-4"><div className="flex min-w-0 flex-1 items-center gap-3"><SidebarTrigger /><span className="text-lg font-medium">Projects</span></div></header>;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== projects.length || $[33] !== selectedProject || $[34] !== withOrg) {
    t13 = <div className="flex h-svh bg-background w-full overflow-hidden flex-col">{t12}<main className="flex flex-1 min-h-0 overflow-hidden">{selectedProject ? <ProjectPanel project={selectedProject} onDelete={setProjectToDelete} withOrg={withOrg} /> : <div className="flex h-full w-full items-center justify-center px-6 text-center"><div><FolderIcon className="mx-auto mb-4 h-10 w-10 text-muted-foreground/40" /><p className="text-muted-foreground text-sm mb-4">{projects.length === 0 ? "Create a project to group chats and give the AI a knowledge base" : "Select a project to view its knowledge base and chats"}</p>{projects.length === 0 && <Button onClick={() => setCreateOpen(true)}>Create your first project</Button>}</div></div>}</main></div>;
    $[32] = projects.length;
    $[33] = selectedProject;
    $[34] = withOrg;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== createOpen || $[37] !== handleCreateSuccess || $[38] !== resolvedOrgId) {
    t14 = <CreateChatProjectDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={handleCreateSuccess} organizationId={resolvedOrgId} />;
    $[36] = createOpen;
    $[37] = handleCreateSuccess;
    $[38] = resolvedOrgId;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = !!projectToDelete;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = open => {
      if (!open) {
        setProjectToDelete(null);
      }
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <AlertDialogTitle>Delete project</AlertDialogTitle>;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = projectToDelete?.name;
  let t19;
  if ($[42] !== t18) {
    t19 = <AlertDialogHeader>{t17}<AlertDialogDescription>Are you sure you want to delete "{t18}"? Its knowledge base files will be removed. Chats in the project are kept but lose the project context.</AlertDialogDescription></AlertDialogHeader>;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <AlertDialogCancel>Cancel</AlertDialogCancel>;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = deleteProject.isPending ? "Deleting..." : "Delete";
  let t22;
  if ($[45] !== deleteProject.isPending || $[46] !== handleDeleteConfirm || $[47] !== t21) {
    t22 = <AlertDialogFooter>{t20}<AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDeleteConfirm} disabled={deleteProject.isPending}>{t21}</AlertDialogAction></AlertDialogFooter>;
    $[45] = deleteProject.isPending;
    $[46] = handleDeleteConfirm;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t19 || $[50] !== t22) {
    t23 = <AlertDialogContent>{t19}{t22}</AlertDialogContent>;
    $[49] = t19;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t15 || $[53] !== t23) {
    t24 = <AlertDialog open={t15} onOpenChange={t16}>{t23}</AlertDialog>;
    $[52] = t15;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t11 || $[56] !== t13 || $[57] !== t14 || $[58] !== t24) {
    t25 = <SidebarProvider>{t11}{t13}{t14}{t24}</SidebarProvider>;
    $[55] = t11;
    $[56] = t13;
    $[57] = t14;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  return t25;
}
interface ProjectPanelProps {
  project: ChatProject;
  onDelete: (project: ChatProject) => void;
  withOrg: (path: string) => string;
}
function ProjectPanel({
  project,
  onDelete,
  withOrg
}: ProjectPanelProps) {
  const {
    data: detailData
  } = useChatProject(project.id);
  const files = detailData?.files as ChatProjectFile[] | undefined ?? [];
  const {
    data: chatsData
  } = useProjectChats(project.id);
  const chats = chatsData?.chats ?? [];
  const updateProject = useUpdateChatProject();
  const uploadFile = useUploadProjectFile();
  const deleteFile = useDeleteProjectFile();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingNames, setUploadingNames] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editInstructions, setEditInstructions] = useState("");
  useEffect(() => {
    setIsEditing(false);
  }, [project.id]);
  const startEdit = () => {
    setEditName(project.name);
    setEditDescription(project.description);
    setEditInstructions(project.instructions);
    setIsEditing(true);
  };
  const handleSave = async () => {
    if (!editName.trim()) {
      return;
    }
    try {
      await updateProject.mutateAsync({
        params: {
          path: {
            id: project.id
          }
        },
        body: {
          name: editName.trim(),
          description: editDescription.trim(),
          instructions: editInstructions.trim()
        }
      });
      setIsEditing(false);
    } catch {
      // error toast handled by useUpdateChatProject
    }
  };
  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList?.length) {
      return;
    }
    for (const file of Array.from(fileList)) {
      const isBinary = isBinaryKnowledgeFile(file.name);
      const maxBytes = isBinary ? PROJECT_BINARY_FILE_MAX_BYTES : PROJECT_FILE_MAX_BYTES;
      if (file.size > maxBytes) {
        toast.error(`${file.name} is larger than ${formatBytes(maxBytes)}`);
        continue;
      }
      let body: {
        name: string;
        mimeType: string;
        content?: string;
        contentBase64?: string;
      };
      if (isBinary) {
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          body = {
            name: file.name,
            mimeType: file.type || "application/octet-stream",
            contentBase64: toBase64(bytes)
          };
        } catch {
          toast.error(`Could not read ${file.name}`);
          continue;
        }
      } else {
        let content: string;
        try {
          content = await file.text();
        } catch {
          toast.error(`Could not read ${file.name} as text`);
          continue;
        }
        if (!content.trim()) {
          toast.error(`${file.name} has no text content`);
          continue;
        }
        body = {
          name: file.name,
          mimeType: file.type || "text/plain",
          content
        };
      }
      setUploadingNames(prev => [...prev, file.name]);
      try {
        await uploadFile.mutateAsync({
          params: {
            path: {
              id: project.id
            }
          },
          body
        });
        toast(`${file.name} added to the knowledge base`);
      } catch {
        // error toast handled by useUploadProjectFile
      } finally {
        setUploadingNames(prev => prev.filter(n => n !== file.name));
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const editNameTooLong = editName.length > PROJECT_NAME_MAX;
  const editDescriptionTooLong = editDescription.length > PROJECT_DESCRIPTION_MAX;
  const isSaveDisabled = !editName.trim() || editNameTooLong || editDescriptionTooLong || updateProject.isPending;
  if (isEditing) {
    return <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
				<div className="flex items-center justify-between border-b px-6 py-4">
					<h2 className="text-lg font-semibold">Edit project</h2>
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
							Cancel
						</Button>
						<Button size="sm" onClick={handleSave} disabled={isSaveDisabled}>
							{updateProject.isPending ? "Saving..." : "Save"}
						</Button>
					</div>
				</div>
				<div className="flex-1 overflow-y-auto p-6 space-y-4">
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="edit-project-name">Name</Label>
							<span className={cn("text-xs tabular-nums", editNameTooLong ? "text-destructive" : "text-muted-foreground")}>
								{editName.length}/{PROJECT_NAME_MAX}
							</span>
						</div>
						<Input id="edit-project-name" value={editName} onChange={e => setEditName(e.target.value)} aria-invalid={editNameTooLong || undefined} />
					</div>
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="edit-project-description">Description</Label>
							<span className={cn("text-xs tabular-nums", editDescriptionTooLong ? "text-destructive" : "text-muted-foreground")}>
								{editDescription.length}/{PROJECT_DESCRIPTION_MAX}
							</span>
						</div>
						<Textarea id="edit-project-description" value={editDescription} onChange={e_0 => setEditDescription(e_0.target.value)} rows={2} aria-invalid={editDescriptionTooLong || undefined} />
					</div>
					<div className="space-y-2">
						<Label htmlFor="edit-project-instructions">Instructions</Label>
						<Textarea id="edit-project-instructions" value={editInstructions} onChange={e_1 => setEditInstructions(e_1.target.value)} placeholder="Custom instructions for every chat in this project..." rows={8} />
					</div>
				</div>
			</div>;
  }
  return <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
			<div className="flex items-center justify-between px-6 py-4">
				<div className="min-w-0 flex-1">
					<h2 className="truncate text-lg font-semibold">{project.name}</h2>
					{project.description && <p className="mt-0.5 text-sm text-muted-foreground">
							{project.description}
						</p>}
				</div>
				<div className="ml-4 flex shrink-0 items-center gap-2">
					<Button size="sm" asChild>
						<Link href={withOrg(`/?project=${project.id}`)}>
							<MessageSquarePlusIcon className="mr-1.5 h-4 w-4" />
							New chat
						</Link>
					</Button>
					<Button variant="ghost" size="sm" onClick={startEdit}>
						Edit
					</Button>
					<Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => onDelete(project)} aria-label="Delete project">
						<TrashIcon className="h-4 w-4" />
					</Button>
				</div>
			</div>
			<div className="px-6">
				<Separator />
			</div>

			<div className="flex-1 overflow-y-auto p-6 space-y-8">
				{/* Knowledge base */}
				<section>
					<div className="mb-3 flex items-center justify-between">
						<div>
							<h3 className="text-sm font-semibold">Knowledge base</h3>
							<p className="text-xs text-muted-foreground">
								Chats in this project answer using these files
							</p>
						</div>
						<Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingNames.length > 0}>
							{uploadingNames.length > 0 ? <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" /> : <UploadIcon className="mr-1.5 h-4 w-4" />}
							{uploadingNames.length > 0 ? "Indexing..." : "Add files"}
						</Button>
						<input ref={fileInputRef} type="file" multiple accept={ACCEPTED_FILE_EXTENSIONS} className="hidden" onChange={e_2 => void handleFilesSelected(e_2.target.files)} />
					</div>

					{files.length === 0 && uploadingNames.length === 0 ? <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full rounded-lg border border-dashed p-8 text-center hover:bg-accent/50 transition-colors">
							<FileTextIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
							<p className="text-sm text-muted-foreground">
								Upload PDF, spreadsheet, text, or markdown files to build the
								knowledge base
							</p>
						</button> : <ul className="space-y-2">
							{uploadingNames.map(name => <li key={`uploading-${name}`} className="flex items-center gap-3 rounded-lg border p-3">
									<Loader2Icon className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium">{name}</div>
										<div className="text-xs text-muted-foreground">
											Chunking and embedding...
										</div>
									</div>
								</li>)}
							{files.map(file_0 => <li key={file_0.id} className="flex items-center gap-3 rounded-lg border p-3">
									{file_0.status === "error" ? <AlertCircleIcon className="h-4 w-4 shrink-0 text-destructive" /> : <FileTextIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
									<div className="min-w-0 flex-1">
										<div className="truncate text-sm font-medium">
											{file_0.name}
										</div>
										<div className="text-xs text-muted-foreground">
											{file_0.status === "error" ? file_0.error ?? "Indexing failed" : `${formatBytes(file_0.size)} · ${file_0.chunkCount} ${file_0.chunkCount === 1 ? "chunk" : "chunks"}`}
										</div>
									</div>
									{file_0.status === "ready" && <Badge variant="secondary" className="shrink-0">
											Indexed
										</Badge>}
									<Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => deleteFile.mutate({
              params: {
                path: {
                  id: project.id,
                  fileId: file_0.id
                }
              }
            })} aria-label={`Delete ${file_0.name}`}>
										<TrashIcon className="h-4 w-4" />
									</Button>
								</li>)}
						</ul>}
				</section>

				{/* Memory */}
				<MemoriesSection projectId={project.id} />

				{/* Instructions */}
				{project.instructions && <section>
						<h3 className="mb-3 text-sm font-semibold">Instructions</h3>
						<p className="whitespace-pre-wrap rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
							{project.instructions}
						</p>
					</section>}

				{/* Chats */}
				<section>
					<h3 className="mb-3 text-sm font-semibold">Chats in this project</h3>
					{chats.length === 0 ? <div className="rounded-lg border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground mb-3">
								No chats yet. Start one to ask questions about your files.
							</p>
							<Button size="sm" asChild>
								<Link href={withOrg(`/?project=${project.id}`)}>
									<MessageSquarePlusIcon className="mr-1.5 h-4 w-4" />
									Start a chat
								</Link>
							</Button>
						</div> : <ul className="space-y-1">
							{chats.map(chat => <li key={chat.id}>
									<Link href={withOrg(`/?id=${chat.id}`)} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-accent transition-colors">
										<MessageSquareIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
										<span className="min-w-0 flex-1 truncate text-sm">
											{chat.title}
										</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{new Date(chat.updatedAt).toLocaleDateString()}
										</span>
									</Link>
								</li>)}
						</ul>}
				</section>
			</div>
		</div>;
}
interface MemoriesSectionProps {
  projectId: string;
}
function MemoriesSection(t0) {
  const $ = _c(42);
  const {
    projectId
  } = t0;
  const {
    data
  } = useProjectMemories(projectId);
  let t1;
  if ($[0] !== data?.memories) {
    t1 = data?.memories as ChatProjectMemory[] | undefined ?? [];
    $[0] = data?.memories;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const memories = t1;
  const createMemory = useCreateProjectMemory();
  const updateMemory = useUpdateProjectMemory();
  const deleteMemory = useDeleteProjectMemory();
  const [newMemory, setNewMemory] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  let t2;
  if ($[2] !== createMemory || $[3] !== newMemory || $[4] !== projectId) {
    t2 = async () => {
      const content = newMemory.trim();
      if (!content || content.length > PROJECT_MEMORY_MAX) {
        return;
      }
      try {
        await createMemory.mutateAsync({
          params: {
            path: {
              id: projectId
            }
          },
          body: {
            content
          }
        });
        setNewMemory("");
      } catch {}
    };
    $[2] = createMemory;
    $[3] = newMemory;
    $[4] = projectId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleAdd = t2;
  let t3;
  if ($[6] !== editContent || $[7] !== projectId || $[8] !== updateMemory) {
    t3 = async memoryId => {
      const content_0 = editContent.trim();
      if (!content_0 || content_0.length > PROJECT_MEMORY_MAX) {
        return;
      }
      try {
        await updateMemory.mutateAsync({
          params: {
            path: {
              id: projectId,
              memoryId
            }
          },
          body: {
            content: content_0
          }
        });
        setEditingId(null);
      } catch {}
    };
    $[6] = editContent;
    $[7] = projectId;
    $[8] = updateMemory;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleSaveEdit = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mb-3"><h3 className="text-sm font-semibold">Memory</h3><p className="text-xs text-muted-foreground">Facts the assistant remembers across chats in this project — saved automatically as you chat, or added here</p></div>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== deleteMemory || $[12] !== editContent || $[13] !== editingId || $[14] !== handleSaveEdit || $[15] !== memories || $[16] !== projectId || $[17] !== updateMemory) {
    t5 = memories.length > 0 && <ul className="mb-3 space-y-2">{memories.map(memory => <li key={memory.id} className="flex items-start gap-3 rounded-lg border p-3"><BrainIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />{editingId === memory.id ? <div className="min-w-0 flex-1 space-y-2"><Textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={2} maxLength={PROJECT_MEMORY_MAX} className="text-sm" /><div className="flex items-center gap-2"><Button size="sm" onClick={() => void handleSaveEdit(memory.id)} disabled={!editContent.trim() || editContent.length > PROJECT_MEMORY_MAX || updateMemory.isPending}>{updateMemory.isPending ? "Saving..." : "Save"}</Button><Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button></div></div> : <><span className="min-w-0 flex-1 text-sm">{memory.content}</span>{memory.source === "auto" && <Badge variant="secondary" className="shrink-0">Auto</Badge>}<Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-muted-foreground" onClick={() => {
            setEditingId(memory.id);
            setEditContent(memory.content);
          }}>Edit</Button><Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => deleteMemory.mutate({
            params: {
              path: {
                id: projectId,
                memoryId: memory.id
              }
            }
          })} aria-label="Delete memory"><TrashIcon className="h-4 w-4" /></Button></>}</li>)}</ul>;
    $[11] = deleteMemory;
    $[12] = editContent;
    $[13] = editingId;
    $[14] = handleSaveEdit;
    $[15] = memories;
    $[16] = projectId;
    $[17] = updateMemory;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => setNewMemory(e_0.target.value);
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== handleAdd) {
    t7 = e_1 => {
      if (e_1.key === "Enter") {
        handleAdd();
      }
    };
    $[20] = handleAdd;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== newMemory || $[23] !== t7) {
    t8 = <Input value={newMemory} onChange={t6} placeholder="Add something the assistant should remember..." maxLength={PROJECT_MEMORY_MAX} onKeyDown={t7} />;
    $[22] = newMemory;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== handleAdd) {
    t9 = () => void handleAdd();
    $[25] = handleAdd;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== createMemory.isPending || $[28] !== newMemory) {
    t10 = !newMemory.trim() || createMemory.isPending;
    $[27] = createMemory.isPending;
    $[28] = newMemory;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== createMemory.isPending) {
    t11 = createMemory.isPending ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <PlusIcon className="mr-1 h-4 w-4" />;
    $[30] = createMemory.isPending;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t9) {
    t12 = <Button variant="outline" size="sm" onClick={t9} disabled={t10}>{t11}Add</Button>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t8) {
    t13 = <div className="flex items-center gap-2">{t8}{t12}</div>;
    $[36] = t12;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13 || $[40] !== t5) {
    t14 = <section>{t4}{t5}{t13}</section>;
    $[39] = t13;
    $[40] = t5;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  return t14;
}